/**
 * The choices a build's owner has for one gear slot, shared by the gear table's select and the
 * character sheet's slot menus so both disable and label the same options.
 */
import { BUILD_SLOT_LABELS, type BuildGearRow, type BuildSlot } from '@thottbot/shared';

export interface GearChoice {
  value: string;
  label: string;
  disabled: boolean;
}

/** Why an option cannot be chosen here: it is already worn in another slot. */
function wornElsewhere(gear: BuildGearRow[], slot: BuildSlot, artifactId: string): BuildSlot | null {
  const other = gear.find((g) => g.slot !== slot && !g.blockedBy && g.selected === artifactId);
  return other ? other.slot : null;
}

/** Whether the owner can switch this slot: more than one option and not emptied by a two-hander. */
export function canChoose(row: BuildGearRow): boolean {
  return row.options.length > 1 && !row.blockedBy;
}

export function gearChoices(row: BuildGearRow, gear: BuildGearRow[]): GearChoice[] {
  const hasOffHand = gear.some((g) => g.slot === 'offHand');
  return row.options.map((o) => {
    const worn = wornElsewhere(gear, row.slot, o.item.artifactId);
    const twoHand = row.slot === 'mainHand' && o.item.twoHand && hasOffHand;
    return {
      value: o.item.artifactId,
      label:
        o.item.name +
        (o.recommended ? ' (recommended)' : '') +
        (worn ? ` (worn in ${BUILD_SLOT_LABELS[worn]})` : '') +
        (twoHand && !worn ? ' (two-hand: empties off hand)' : ''),
      disabled: !!worn,
    };
  });
}

/** The selection to save for a chosen value: `null` when it is the agent's pick. */
export function selectionFor(row: BuildGearRow, value: string): string | null {
  return row.options.find((o) => o.recommended)?.item.artifactId === value ? null : value;
}
